import { zodResolver } from '@hookform/resolvers/zod';
import { useRef } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { z } from 'zod';
import { FormErrorMessage } from '@/components/form/form-error-message';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { PurchaseOrderSendRecipients } from '@/features/purchase-order/purchase-order-send-recipients';
import { useSendProductionDeclaration } from '@/services/apis/mutations/purchase-orders/use-send-production-declaration';

const sendProductionDeclarationSchema = z.object({
  recipients: z
    .array(z.email({ error: 'sendPoModalInvalidRecipient' }))
    .min(1, 'sendPoModalNoRecipient'),
  message: z.string().trim().min(1, 'sendProductionDeclarationMessageRequired'),
});

type PurchaseOrderMainProductionDeclarationEmailDialogProps = {
  onOpenChange: (open: boolean) => void;
  purchaseOrderId: string;
  initialRecipients: string[];
  initialRecipientLabels: Record<string, string>;
  initialMessage: string;
};

export function PurchaseOrderMainProductionDeclarationEmailDialog({
  onOpenChange,
  purchaseOrderId,
  initialRecipients,
  initialRecipientLabels,
  initialMessage,
}: PurchaseOrderMainProductionDeclarationEmailDialogProps) {
  const { t } = useTranslation('purchaseOrder');
  const sendDeclaration = useSendProductionDeclaration(purchaseOrderId);
  const sendingRef = useRef(false);
  const form = useForm<z.infer<typeof sendProductionDeclarationSchema>>({
    resolver: zodResolver(sendProductionDeclarationSchema),
    defaultValues: { recipients: initialRecipients, message: initialMessage },
  });
  const isSending = sendDeclaration.isPending || form.formState.isSubmitting;

  const onSubmit = form.handleSubmit(async (payload) => {
    if (sendingRef.current) {
      return;
    }
    sendingRef.current = true;
    try {
      await sendDeclaration.mutateAsync(payload);
      toast.success(t('sendProductionDeclarationSuccess'));
      onOpenChange(false);
    } catch {
      toast.error(t('sendProductionDeclarationError'));
    } finally {
      sendingRef.current = false;
    }
  });

  return (
    <Dialog
      onOpenChange={(open) => {
        if (!sendingRef.current) {
          onOpenChange(open);
        }
      }}
      open
    >
      <DialogContent className='sm:max-w-3xl' showCloseButton={!isSending}>
        <DialogHeader>
          <DialogTitle>{t('sendProductionDeclaration')}</DialogTitle>
        </DialogHeader>
        <form className='flex flex-col gap-4' onSubmit={onSubmit}>
          <fieldset className='flex flex-col gap-4' disabled={isSending}>
            <Controller
              control={form.control}
              name='recipients'
              render={({ field, fieldState }) => (
                <div>
                  <PurchaseOrderSendRecipients
                    isSending={isSending}
                    onChange={field.onChange}
                    open
                    recipientLabels={initialRecipientLabels}
                    recipients={field.value}
                  />
                  {fieldState.error?.message && (
                    <FormErrorMessage
                      error={fieldState.error.message}
                      ns='purchaseOrder'
                    />
                  )}
                </div>
              )}
            />
            <div className='flex flex-col gap-2'>
              <Label htmlFor='production-declaration-message'>
                {t('sendPoModalMessage')}
              </Label>
              <Textarea
                className='field-sizing-fixed max-h-60 min-h-24 overflow-y-auto'
                id='production-declaration-message'
                rows={8}
                {...form.register('message')}
              />
              {form.formState.errors.message?.message && (
                <FormErrorMessage
                  error={form.formState.errors.message.message}
                  ns='purchaseOrder'
                />
              )}
            </div>
          </fieldset>
          <DialogFooter>
            <DialogClose asChild>
              <Button disabled={isSending} type='button' variant='outline'>
                {t('cancel', { ns: 'common' })}
              </Button>
            </DialogClose>
            <Button disabled={isSending} type='submit'>
              {t('sendPoModalSend')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
